/* eslint-disable no-underscore-dangle */
import debounce from 'lodash/debounce';
import { createElement, installComponent } from '../utils/createElement';
import { BREAKPOINTS } from '../utils/breakpoints';
import { COLOR_GRAY_BASE, COLOR_DARK_2, COLOR_DARK_5, COLOR_SCROLLBAR, COLOR_GRAY_900, COLOR_OVERLAY, COLOR_LIGHT, COLOR_WHITE, COLOR_LIGHT_GRAY, COLOR_LIGHT_GRAY_2 } from '../utils/colors';
import { closeAllTabs } from '../utils/closeAllTabs';

const ICON_LINK = 'https://i.icomoon.io/public/396efb577d/Nav/symbol-defs.svg';

export default class SideNav extends HTMLElement {
  constructor() {
    super();

    this._links = [];

    this._footerItems = [];

    this.symbols = null;
    this.linksLoaded = false;
    this.symbolsLoaded = false;

    this._loadingLinkCount = 5;

    this._expanded = false;

    this.attachShadow({ mode: 'open' });

    this.boundPopupEventListener = this.popupEventListener.bind(this);
    this.boundPartialDisable = debounce(this.partialDisable.bind(this), 500, { leading: true, trailing: false });
    this.boundSetToggle = debounce(this.setToggle.bind(this), 500, { leading: false });
    this.boundSetupListeners = (e) => this.setActiveLink(e);
    this.linkClickEvent = (e) => {
      const link = e?.detail?.link;
      if (link) this.setActiveLink({ newURL: link });
    };
  }

  set fetchingLinks(promise) {
    promise.then((res) => {
      this._links = res.body ? res.body : res;
      this._footerItems = res.footer || [];
      this.linksLoaded = true;

      this.renderChildren();

      // Fix mobile view when admin bar is showing.
      const sideNav = document.querySelector('sb-sidenav');
      const nav = sideNav?.shadowRoot?.querySelector('.sb-sidenav');
      const [{ bottom, height }] = nav ? nav.getClientRects() : [{}];
      const offset = bottom - height;
      if (offset > 0) {
        if (window.innerWidth <= BREAKPOINTS.lg) {
          sideNav.shadowRoot.querySelector('.sb-sidenav__footer-wrapper').style.translate = `0 -${offset}px`;
        }

        document.querySelector(':root').style.setProperty('--offset-height', `${offset}px`);
      } else {
        document.querySelector(':root').style.setProperty('--offset-height', '0px');
      }
    });
  }

  get title() {
    return this.getAttribute('title') || 'ShipBob';
  }

  get appUrl() {
    return this.getAttribute('appUrl') || this.getAttribute('app-url') || '#';
  }

  get resourceUrl() {
    return this.getAttribute('resourceUrl') || this.getAttribute('resource-url') || '';
  }

  get emitEvent() {
    return this.getAttribute('emitEvent') || this.getAttribute('emit-event') || false;
  }

  connectedCallback() {
    this.setAttribute('data-sb-automation', 'sb-sidenav');

    const parentStyleSheet = createElement('style', null, `
      .sb-app-container {
        min-height: 100dvh;
        display: flex;
        align-items: stretch;
        flex-direction: row;
      }
      .sb-app-container__main {
        flex-grow: 2;
        margin-left: 256px;
        width: calc(100% - 256px);
      }

      sb-sidenav {
        z-index: 800;
      }

      @media only screen and (max-width: ${BREAKPOINTS.lg}px) and (min-width: ${BREAKPOINTS.md}px) {
        .sb-app-container__main {
          margin: 57px 0 0 0;
          width: calc(100% - 60px);
        }
      }

      @media only screen and (max-width: ${BREAKPOINTS.md}px) {
        .sb-app-container {
          flex-direction: column;
        }
        .sb-app-container__main {
          margin: 57px 0 0 0;
          width: 100%;
        }
        
        .sb-sidenav-link--full-screen--active > .sb-sidenav-separator {
          margin: 20px;
        }
      }
    `);
    this.parentElement.append(parentStyleSheet);

    window.addEventListener('resize', this.boundPartialDisable);
    window.addEventListener('resize', this.boundSetToggle);
    document.body.addEventListener('click', this.boundPopupEventListener);

    const headerSlot = createElement('slot', { name: 'header' });
    this.header = createElement('sb-sidenav-header', { title: this.title, appUrl: this.appUrl, resourceUrl: this.resourceUrl }, [headerSlot]);
    this.headerWrapper = createElement('div', { class: 'sb-sidenav__header-wrapper' }, [this.header]);

    this.headerWrapper.addEventListener('toggle-sidenav', (e) => {
      e.stopPropagation();
      this.toggleSidenav();
    });

    this.body = createElement('div', { class: 'sb-sidenav__body' });

    this.footer = createElement('sb-sidenav-footer', { name: 'sb-sidenav-footer' });
    this.footerWrapper = createElement('div', { class: 'sb-sidenav__footer-wrapper' }, [this.footer]);

    const animationSpeed = window.innerWidth <= BREAKPOINTS.md || window.innerWidth >= BREAKPOINTS.lg ? 150 : 30;

    const styles = createElement('style', null, `
      @import url(https://use.typekit.net/oob2lfs.css);
      .sb-sidenav {
        --app-height: 100dvh;
        max-height: 100dvh;
        height: 100dvh;
        width: 256px;
        background-color: ${COLOR_DARK_2};
        display: flex;
        position: fixed;
        flex-direction: column;
        transition: transform ${animationSpeed}ms ease;
      }
      .sb-sidenav__body {
        position: relative;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        overflow-x: hidden;
        transition: all ${animationSpeed}ms ease;
        opacity: 1;
        background-color: ${COLOR_DARK_2};
        height: calc(100dvh - 115px - var(--offset-height));
      }

      .sb-sidenav__body--with-custom-footer {
        height: calc(100dvh - 95px - var(--custom-footer-height) - var(--offset-height));
      }

      .sb-sidenav__footer-wrapper {
        flex: 0 0 auto;
        overflow: hidden;
        transition: transform ${animationSpeed}ms ease;
        bottom: 0;
        position: fixed;
        width: 256px;
        z-index: 4;
      }
      .sb-sidenav__body--hidden {
        opacity: 0;
      }
      .sb-sidenav-link {
        width: 100%;
      }
      .sub-links-container {
        z-index: -1;
        opacity: 0;
        top: 0px;
      }

      .sub-links-container:not(.sub-links-container--expand-popover) .sub-links:not(.sb-sidenav-link--full-screen--active) {
        height: 0;
      }

      .sub-links-container--expand-popover.sub-links-container {
        position: relative;
        top: -30px;
      }
      .sub-links-container--transition {
        transition: opacity ${animationSpeed}ms;
      }
      .sub-links {
        position: absolute;
        transition: transform ${animationSpeed}ms;
        background: ${COLOR_DARK_5};
        margin: 0 12px;
        border-radius: 0 0 6px 6px;
        transform: translateY(-2px);
        overflow: hidden;
      }
      .account-switch .sub-links {
        background: ${COLOR_WHITE};
      }
      .account-switch .hide {
        display: none;
      }

      .sub-links-popover-arrow, .sub-links-popover-arrow::before {
        position: absolute;
        width: 16px;
        height: 16px;
        left: -4px;
        z-index: -1;
      }

      .sub-links-popover-arrow[data-hide] {
        visibility: hidden;
      }

      .sub-links-popover-arrow::before {
        background: ${COLOR_DARK_2};
        content: '';
        transform: rotate(45deg);
      }

      .account-switch .sub-links-popover-arrow::before {
        background: ${COLOR_WHITE};
      }

      .sb-sidenav__overlay {
        position: absolute;
        top: 0;
        left: 0;
      }

      .sb-sidenav__overlay--active {
        width: 100vw;
        height: 100dvh;
        background: ${COLOR_OVERLAY};
      }

      @keyframes expand-animation {
        0% {
          opacity: 0;
          z-index: 0;
        }
        99% {
          opacity: 1;
          z-index: 0;
        }
        100% {
          opacity: 1;
          z-index: 1;
        }
      }
      @keyframes collapse-animation {
        0% {
          opacity: 1;
          z-index: 1;
        }
        99% {
          opacity: 0;
        }
        100% {
          opacity: 0;
          z-index: -1;
        }
      }
      .sub-links-container--expand {
        animation-name: expand-animation;
        animation-duration: ${animationSpeed}ms;
        animation-fill-mode: forwards;
      }
      .sub-links-container--collapse {
        animation-name: collapse-animation;
        animation-duration: ${animationSpeed}ms;
        animation-fill-mode: forwards;
      }

      @keyframes expand-popover-animation {
        0% {
          opacity: 0;
          z-index: 1;
        }
        100% {
          opacity: 1;
          z-index: 1;
        }
      }
      @keyframes collapse-popover-animation {
        0% {
          opacity: 1;
          z-index: 1;
        }
        99% {
          opacity: 0;
        }
        100% {
          opacity: 0;
          z-index: -1;
        }
      }
      .sub-links-container--expand-popover {
        animation-name: expand-popover-animation;
        animation-duration: ${animationSpeed}ms;
        animation-fill-mode: forwards;
      }
      .sub-links-container--expand-popover .sub-links {
        background-color: ${COLOR_DARK_2};
        border-radius: 5px;
        overflow: initial;
        white-space: nowrap;
      }
      .sub-links-container--expand-popover.account-switch .sub-links{
        box-shadow: 0 0.1rem 10rem 0 ${COLOR_DARK_2};
        background-color: ${COLOR_WHITE};
        border-radius: 5px;
        overflow: initial;
        white-space: nowrap;
        width: 320px;
      }

      .sub-links-container--expand-popover.account-switch .account-switch-sub-menu {
        height: 250px;
        overflow: auto;
      }

      .sub-links-container--expand-popover.account-switch .account-switch-sub-menu::-webkit-scrollbar {
        width: 7px;
      }
 
      .sub-links-container--expand-popover.account-switch .account-switch-sub-menu::-webkit-scrollbar-thumb {
        background: ${COLOR_LIGHT_GRAY_2}; 
        border-radius: 5px;
      }

      .sub-links-container--expand-popover.account-switch .account-switch-sub-menu::-webkit-scrollbar-thumb:hover {
        background: ${COLOR_GRAY_900}; 
      }

      .sub-links-container--expand-popover .sub-links:has(.sb-sidenav-title) {
        padding-bottom: 1rem;
      }

      .sub-links-container--expand-popover .sub-links:has([name="Activity Log"]) {
        padding-bottom: 0;
      }

      .sub-links-container--expand-popover .sub-links:last-child .sb-sidenav-link--sub {
        padding-bottom: 1rem;
      }

      .sb-sidenav-title {
        font-size: 1.375rem;
        color: white;
        padding-left: 24px;
        margin: 12px 0;
      }
      .sb-sidenav-sub-title {
        color: white;
        padding-left: 24px;
        margin-top: 20px;
        margin-bottom: 7px;
        margin-right: 15px;
      }
      .account-switch .sb-sidenav-title,
      .account-switch .sb-sidenav-sub-title{
        color: ${COLOR_DARK_2};
      }

      sb-sidenav-link + .sb-sidenav-sub-title {
        margin-top: 13px;
      }
      .sub-links-container--collapse-popover {
        animation-name: collapse-popover-animation;
        animation-duration: ${animationSpeed}ms;
        animation-fill-mode: forwards;
      }

      .sub-links-container--collapse-popover .sub-links {
        height: 0;
      }

      .sb-sidenav__body::-webkit-scrollbar {
        width: 0.01px;
        height: 0.01px;
      }
      .sb-sidenav__body::-webkit-scrollbar-track {
        -webkit-box-shadow: none;
        border-radius: 0;
      }
      .sb-sidenav__body::-webkit-scrollbar-thumb {
        background: ${COLOR_SCROLLBAR};
        opacity: 0.5;
        -webkit-box-shadow: none;
      }
      .sb-sidenav__body::-webkit-scrollbar-thumb:hover {
        background: ${COLOR_GRAY_BASE};
      }

      .sb-sidenav__footer-wrapper--expanded {
        transform: translateX(256px);
      }

      .sb-sidenav-separator {
        justify-content: center;
        display: flex;
        transition: transform ${animationSpeed}ms ease;
        margin: 0.5rem 0;
      }

      .account-switch.sb-sidenav-separator {
        margin: 0;
      }

      .sb-sidenav-separator div {
        width: 80%;
        border-top: 1px solid ${COLOR_GRAY_900};
      }

      .account-switch .sb-sidenav-separator div {
        width: 100%;
        border-top: 1px solid ${COLOR_LIGHT};
      }
        
      .account-switch-search-container{
        padding: 7px 14px;
        background-color: ${COLOR_LIGHT_GRAY};
      }

      .account-switch-search{
        width: 100%;
        height: 36px;
        box-sizing: border-box;
        border: 2px solid ${COLOR_LIGHT};
        border-radius: 4px;
        background-color: white;
        background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath d='M15.6344 13.8657L11.8938 10.125C12.7444 8.86221 13.1641 7.28439 12.9394 5.60313C12.5563 2.74344 10.2125 0.416241 7.34999 0.0538332C3.09404 -0.4847 -0.484725 3.09406 0.0538391 7.35002C0.41634 10.2138 2.74385 12.5594 5.60386 12.9407C7.28512 13.1653 8.86325 12.7458 10.1258 11.895L13.8664 15.6357C14.3545 16.1238 15.1461 16.1238 15.6342 15.6357C16.1219 15.1469 16.1219 14.3532 15.6344 13.8657ZM2.47185 6.50001C2.47185 4.29438 4.26623 2.5 6.47186 2.5C8.6775 2.5 10.4719 4.29438 10.4719 6.50001C10.4719 8.70565 8.6775 10.5 6.47186 10.5C4.26623 10.5 2.47185 8.70627 2.47185 6.50001Z' fill='grey' /%3E%3C/svg%3E") no-repeat 13px center;
        background-repeat: no-repeat;
        padding: 5px 5px 5px 35px;
      }

      @media only screen and (max-width: ${BREAKPOINTS.lg}px) and (min-width: ${BREAKPOINTS.md}px) {
        .sb-sidenav {
          width: 60px;
          transform: translateX(0px);
        }

        .sb-sidenav__body > .sb-sidenav-sub-title {
          color: white;
          margin: 12px 0;
          font-size: 10px;
          text-align: center;
          text-overflow: ellipsis;
          padding: 0 2px;
        }
        .sb-sidenav__footer-wrapper--expanded {
          transition: transform 0ms ease;
          transform: translateX(0px);
        }
        .sub-links {
          transition: transform 0ms;
        }

        .sb-sidenav__footer-wrapper {
          width: 60px;
        }

        .sb-sidenav-separator div {
          width: 40%;
        }

        .sb-sidenav-title + .sb-sidenav-separator div {
          width: 80%;
        }

        .sb-sidenav__body {
          height: calc(100dvh - 170px - var(--offset-height));
        }

        .sb-sidenav {
          width: 319px;
          transform: translateX(-319px);
          transition: transform 0ms ease;
        }

        .sb-sidenav__header-wrapper {
          animation-name: reveal-topnav;
          animation-delay: ${animationSpeed}ms;
          animation-duration: ${animationSpeed}ms;
          animation-fill-mode: forwards;
        }

        .sb-sidenav__body {
          margin-top: -1px;
          padding: 20px;
          padding-top: 4rem;
          height: calc(100dvh - 115px - var(--offset-height));
        }

        .sb-sidenav__body--with-custom-footer {
          height: calc(100dvh - 75px - var(--custom-footer-height) - var(--offset-height));
        }

        .sb-sidenav__body:has(.sb-sidenav-link--full-screen--active) {
          height: 100%;
        }

        .sb-sidenav-link--full-screen--active > .sb-sidenav-separator {
          margin: 20px 0;
        }

        .sb-sidenav-separator div, .sb-sidenav-link--full-screen--active > .sb-sidenav-separator div {
          width: 96%;
        }

        .sb-sidenav--expanded {
          transform: translateX(0);
        }

        .sb-sidenav__footer-wrapper {
          width: 319px;
        }

        .sub-links {
          margin: 0;
        }

        .sub-links-container--expand-popover .sub-links {
          margin: 0 0 0 330px;
        }

        .sb-sidenav-mobile-close {
          cursor: pointer;
          position: absolute;
          left: calc(319px - 58px);
          top: 24px;
          z-index: 1;
          width: 24px;
          height: 24px;
          display: flex;
          justify-content: center;
        }

        .sb-sidenav-mobile-close div{
          height: 17px;
          width: 2px;
          background: white;
          position: absolute;
        }

        .sb-sidenav-mobile-close div:first-child {
          transform: rotate(45deg);
        }

        .sb-sidenav-mobile-close div:nth-child(2) {
          transform: rotate(315deg);
        }

        .sb-sidenav-link--full-screen {
          transform: translateY(100dvh);
          animation-name: hide-full-screen;
          animation-duration: ${animationSpeed}ms;
          animation-fill-mode: forwards;
        }

        .sb-sidenav-link--full-screen--active {
          position: absolute;
          top: 0;
          left: 0;
          height: 100dvh;
          transform: translateY(0);
          animation-name: reveal-full-screen;
          animation-duration: ${animationSpeed}ms;
          animation-fill-mode: forwards;
          width: 100vw;
          padding: 20px;
          overflow-y: auto;
          box-sizing: border-box;
          padding-bottom: 10vh;
        }

        .sb-sidenav-title {
          font-size: 24px;
          margin-top: 10px;
          margin-bottom: 15px;
        }

        .sb-sidenav-title, .sb-sidenav-sub-title {
          padding-left: 24px;
          font-size: 1.375rem;
        }
      }

      @media only screen and (min-width: ${BREAKPOINTS.lg}px)) {
        .sb-sidenav__body {
          height: calc(100dvh - 115px) - var(--offset-height);
        }
      }
      @media only screen and (max-width: ${BREAKPOINTS.md}px) {
        .sb-sidenav {
          width: 100vw;
          transform: translateX(-100vw);
          transition: transform 0ms ease;
        }

        .sb-sidenav__body--expanded {
          transform: translateX(256px);
        }

        .sb-sidenav__header-wrapper {
          animation-name: reveal-topnav;
          animation-delay: ${animationSpeed}ms;
          animation-duration: ${animationSpeed}ms;
          animation-fill-mode: forwards;
        }

        .sub-links-popover-arrow, .sub-links-popover-arrow::before {
          display: none
        }

        .sub-links {
          height: 0;
        }
        .sub-links-container--expand-popover .sub-links {
          height: 100%;
        }

        .sub-links-container--expand .sub-links {
          height: 100%;
        }
          
        .sub-links-container--expand .sb-sidenav-link--full-screen--active {
            border-radius: 0;  
        }
          
        .account-switch.sub-links-container--expand .sb-sidenav-link--full-screen--active {
          border-radius: 0;
          padding: 50px 0 0 0;
          background-color: ${COLOR_LIGHT_GRAY};
        }

        @media only screen and (max-height: ${BREAKPOINTS.sm}px) {
          .sub-links-container--expand .sub-links {
            padding-bottom: 0;
          }
        }

        .sb-sidenav__body {
          margin-top: -1px;
          padding: 20px;
          padding-top: 4rem;
          height: calc(100dvh - 115px - var(--offset-height));
        }

        .sb-sidenav__body--with-custom-footer {
          height: calc(100dvh - 75px - var(--custom-footer-height) - var(--offset-height));
        }

        .sb-sidenav__body:has(.sb-sidenav-link--full-screen--active) {
          height: 100%;
        }

        .sb-sidenav-link--full-screen--active > .sb-sidenav-separator {
          margin: 20px 0;
        }

        .sb-sidenav-link--full-screen--active > .account-switch.sb-sidenav-separator {
          margin: 0;
        }

        .sb-sidenav-separator div {
          width: 96%;
        }

        .sb-sidenav-link--full-screen--active > .sb-sidenav-separator div {
          width: 86%;
        }

        .sb-sidenav-link--full-screen--active > .account-switch.sb-sidenav-separator div {
          width: 100%;
        }

        .sb-sidenav__body--expanded {
          transform: translateX(100vw);
        }

        .sb-sidenav__footer-wrapper {
          width: 100vw;
        }

        .sb-sidenav__footer-wrapper--expanded {
          transform: translateX(100vw);
        }

        .sub-links {
          margin: 0;
        }

        .sb-sidenav-mobile-close {
          cursor: pointer;
          position: absolute;
          left: calc(100vw - 58px);
          top: 24px;
          z-index: 1;
          width: 24px;
          height: 24px;
          display: flex;
          justify-content: center;
        }

        .sb-sidenav-mobile-close div{
          height: 17px;
          width: 2px;
          background: white;
          color: white;
          mix-blend-mode: color;
          position: absolute;
        }

        .account-switch .sb-sidenav-mobile-close div{
          background: #2470af;
        }

        .sb-sidenav-mobile-close div:first-child {
          transform: rotate(45deg);
        }

        .sb-sidenav-mobile-close div:nth-child(2) {
          transform: rotate(315deg);
        }

        .sb-sidenav-link--full-screen {
          transform: translateY(100dvh);
          animation-name: hide-full-screen;
          animation-duration: ${animationSpeed}ms;
          animation-fill-mode: forwards;
        }

        .sb-sidenav-link--full-screen--active {
          position: absolute;
          top: 0;
          left: 0;
          height: 100dvh;
          transform: translateY(0);
          animation-name: reveal-full-screen;
          animation-duration: ${animationSpeed}ms;
          animation-fill-mode: forwards;
          width: 100vw;
          padding: 20px 0;
          overflow-y: auto;
          box-sizing: border-box;
        }

        .sb-sidenav-title {
          font-size: 24px;
          margin-top: 0;
          margin-bottom: 15px;
        }

        .sb-sidenav-title, .sb-sidenav-sub-title {
          padding-left: 28px;
          font-size: 1.375rem;
        }
      }
      @keyframes reveal-topnav {
        0% {
          transform: translate(100vw, -50px);
          width: 100vw;
        }
        100% {
          transform: translate(100vw, 0px);
          width: 100vw;
        }
      }

      @keyframes reveal-full-screen {
        0% {
          transform: translateY(100vw);
        }
        100% {
          transform: translateY(0);
        }
      }

      @keyframes hide-full-screen {
        0% {
          transform: translateY(0);
        }
        100% {
          transform: translateY(100vw);
        }
      }
    `);

    const overlay = createElement('div', { class: 'sb-sidenav__overlay' });

    this.root = createElement('div', { class: 'sb-sidenav' }, [this.headerWrapper, overlay, this.body, this.footerWrapper]);
    this.shadowRoot.append(this.root, styles);

    this.renderLoader();

    this.getIcons().then((icons) => {
      this.renderIcons(icons);
    });

    this.setAppHeight = this.setAppHeight.bind(this);
    window.addEventListener('resize', debounce(this.setAppHeight, 100));
    window.addEventListener('hashchange', this.boundSetupListeners);
    window.addEventListener('link-clicked', this.linkClickEvent);

    this.setAppHeight();
  }

  disconnectedCallback() {
    // Remove event listeners added in connectedCallback
    window.removeEventListener('resize', this.boundPartialDisable);
    window.removeEventListener('resize', this.boundSetToggle);
    window.removeEventListener('hashchange', this.boundSetupListeners);
    window.removeEventListener('link-clicked', this.linkClickEvent);
    document.body.removeEventListener('click', this.boundPopupEventListener);
  }

  popupEventListener(event) {
    const sideNav = document.querySelector('sb-sidenav')?.shadowRoot;
    if (!sideNav) return;

    const hasPopoverActive = sideNav.querySelector('.sub-links-container--expand-popover');
    const popoverLink = sideNav.querySelector('sb-sidenav-link[type="popover"]');
    if (!popoverLink || !popoverLink.shadowRoot) return;

    const activeLink = popoverLink.shadowRoot.querySelector('.sb-sidenav-link--active');
    if (!activeLink) return;

    const isOutsideSideNav = event.target.tagName !== 'SB-SIDENAV';
    if (isOutsideSideNav && hasPopoverActive) {
      activeLink.click();
    }
  }

  renderChildren() {
    if (!this.linksLoaded || !this.symbolsLoaded) return;

    this.renderLinks(this._links);
    this.setToggle();
    this.setupListeners();

    if (this._footerItems.length) {
      this.renderCustomFooter();
    }
  }

  setActiveLink(e) {
    const url = e?.newURL || window.location.href;
    let links = [];

    if (typeof this.body.querySelectorAll === 'function') {
      links = this.body.querySelectorAll('sb-sidenav-link');
    }

    const possibleMatches = [];

    links.forEach((link) => {
      link.shadowRoot.querySelector('a').classList.remove('sb-sidenav-link--sub--active');

      if (link.link && url.toLowerCase().includes(link.link?.toLowerCase())) {
        possibleMatches.push(link);
      }
    });

    if (possibleMatches.length > 1) {
      // If there is more than 1 match, it will prioritize the longest match.
      const longestMatch = possibleMatches.reduce((a, b) => {
        return a.link.length > b.link.length ? a : b;
      });

      longestMatch.addStyleForActiveLink();
    } else {
      possibleMatches[0]?.addStyleForActiveLink();
    }
  }

  renderLinks(links) {
    // eslint-disable-next-line no-restricted-syntax
    for (const loader of this.body.children) {
      loader.loadingRoot.classList.add('sb-sidenav-loading-link--remove');
    }

    const fragment = document.createDocumentFragment();

    const closeMobileButton = createElement(
      'div',
      { class: 'sb-sidenav-mobile-close' },
      [
        createElement('div'),
        createElement('div'),
      ],
    );

    const header = document.querySelector('sb-sidenav').shadowRoot.querySelector('sb-sidenav-header');
    closeMobileButton.addEventListener('click', header.activatorEvent);

    this.body.appendChild(closeMobileButton);

    const { emitEvent } = this;

    links.forEach(({ name, icon, link, children, type = 'normal', selected, tags }) => {
      if (type === 'separator') {
        const sidenavLink = createElement('div', { class: 'sb-sidenav-separator' });
        if (tags && tags.includes('account-switch')) {
          sidenavLink.classList.add('account-switch');
        }
        sidenavLink.appendChild(createElement('div'));
        fragment.appendChild(sidenavLink);
        return;
      } else if (type === 'title') {
        fragment.appendChild(createElement('h3', { class: 'sb-sidenav-title' }, name));
        return;
      } else if (type === 'subTitle') {
        fragment.appendChild(createElement('h4', { class: 'sb-sidenav-sub-title' }, name));
        return;
      }

      const hasChildren = children.length > 0;
      const variation = hasChildren ? 'parent' : 'primary';
      const sidenavLink = createElement('sb-sidenav-link', { name, icon, link, variation, type, selected, tags, emitEvent });
      fragment.appendChild(sidenavLink);
      if (hasChildren) {
        const subMenuContainer = createElement('div', { class: 'account-switch-sub-menu' });
        const subLinks = children.map(({ name, icon, link, type = 'normal', selected, children, tags }) => {
          let sidenavLinkItem;
          switch (type) {
            case 'separator':
              sidenavLinkItem = createElement('div', { class: 'sb-sidenav-separator' });
              if (tags && tags.includes('account-switch')) {
                sidenavLinkItem.classList.add('account-switch');
              }
              sidenavLinkItem.appendChild(createElement('div'));
              break;
            case 'title':
              sidenavLinkItem = createElement('h3', { class: 'sb-sidenav-title' }, name);
              break;
            case 'subTitle':
              sidenavLinkItem = createElement('h4', { class: 'sb-sidenav-sub-title' }, name);
              break;
            case 'account-switch-sub-menu':
              children.forEach((sub) => {
                const { name, icon, link, type, selected, tags } = sub;
                sidenavLinkItem = createElement('sb-sidenav-link', { name, icon, link, variation: 'sub', type, selected, tags, emitEvent });
                subMenuContainer.appendChild(sidenavLinkItem);
              });
              sidenavLinkItem = subMenuContainer;
              break;
            default:
              sidenavLinkItem = createElement('sb-sidenav-link', { name, icon, link, variation: 'sub', type, selected, tags, emitEvent });
              if (tags && tags.includes('footer')) {
                sidenavLinkItem.classList.add('account-switch-footer');
              }
              break;
          }
          return sidenavLinkItem;
        });
        const subLinkContainer = createElement('div', { class: 'sub-links', name: `sub${name}` }, subLinks);
        this.tryAddSubAccountFilter(type, subLinkContainer);
        const con = createElement('div', { name: `sub${name}`, class: 'sub-links-container sub-links-container--transition' }, subLinkContainer);
        if (type === 'account-switch') {
          con.classList.add(type);
        }
        subLinkContainer.appendChild(createElement('div', { class: 'sub-links-popover-arrow', 'data-popper-arrow': '' }));
        fragment.appendChild(con);
      }
    });

    this.body.appendChild(fragment);

    this.iterateLinks((sidenavLink) => {
      if (sidenavLink.root) sidenavLink.root.classList.add('sb-sidenav-link--activate');
    });
  }

  tryAddSubAccountFilter(type, subLinkContainer) {
    if (type === 'account-switch') {
      const separator = createElement('div', { class: 'sb-sidenav-separator account-switch' }, createElement('div'));
      subLinkContainer.prepend(separator);
      const searchInput = createElement('input', { type: 'text', class: 'account-switch-search', placeholder: 'Search' });
      const searchInputContainer = createElement('div', { class: 'account-switch-search-container' }, searchInput);
      const debouncedFilter = debounce(this.filterSubaccounts, 500);
      searchInput.addEventListener('input', (event) => {
        debouncedFilter(event.currentTarget);
      });
      subLinkContainer.prepend(searchInputContainer);
    }
  }

  renderCustomFooter() {
    const footerDiv = this.footer?.shadowRoot?.querySelector('.sb-sidenav-footer');

    if (footerDiv) {
      footerDiv.innerHTML = '';
      footerDiv.classList.add('sb-sidenav-custom-footer');
    }

    for (let i = 0; i < this._footerItems.length; i += 1) {
      if (this._footerItems[i].desktop || this._footerItems[i].tablet || this._footerItems[i].mobile) {
        if (this._footerItems[i].desktop === 'row') footerDiv?.classList.add('sb-sidenav-custom-footer-row-desktop');
        if (this._footerItems[i].tablet === 'row') footerDiv?.classList.add('sb-sidenav-custom-footer-row-tablet');
        if (this._footerItems[i].mobile === 'column') footerDiv?.classList.add('sb-sidenav-custom-footer-column-mobile');
      } else {
        const itemText = createElement('div', { class: 'sb-sidenav-custom-footer-item-text' }, this._footerItems[i].name);
        const itemIcon = createElement('div', { class: 'sb-sidenav-icon' });

        if (this._footerItems[i].icon) {
          const selectedIcon = this.symbols?.querySelector(`#icon-${this._footerItems[i].icon}`);

          itemIcon.innerHTML = `
          <svg class="sb-sidenav-icon__sprite" viewBox="${selectedIcon?.getAttribute('viewBox')}">
            <g>
              ${selectedIcon?.innerHTML}
            </g>
          </svg>`;
        }

        const itemAnchor = createElement('a', { class: 'sb-sidenav-custom-footer-item', href: this._footerItems[i].link, 'data-sb-automation': this._footerItems[i].name }, [itemIcon, itemText]);

        footerDiv?.appendChild(itemAnchor);

        const sideNav = document.querySelector('sb-sidenav')?.shadowRoot?.querySelector('.sb-sidenav');
        const customFooterHeight = sideNav?.querySelector('.sb-sidenav__footer-wrapper').offsetHeight;

        document.querySelector(':root').style.setProperty('--custom-footer-height', `${customFooterHeight}px`);
        this.body?.classList?.add('sb-sidenav__body--with-custom-footer');
      }
    }
  }

  renderIcons(icons) {
    this.symbols = document.createElement('div');
    this.symbols.innerHTML = icons;
    this.symbolsLoaded = true;
    this.shadowRoot.append(this.symbols);

    this.renderChildren();
  }

  renderLoader() {
    const fragment = document.createDocumentFragment();
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < this._loadingLinkCount; i++) {
      fragment.appendChild(createElement('sb-sidenav-loading-link', {}));
    }
    this.body.appendChild(fragment);
  }

  async getIcons() {
    return new Promise((resolve) => {
      const xhttp = new XMLHttpRequest();
      xhttp.onreadystatechange = function () {
        // eslint-disable-next-line eqeqeq
        if (this.readyState == 4) {
          // eslint-disable-next-line eqeqeq
          if (this.status == 200) resolve(this.response);
          else resolve(null);
        }
      };
      xhttp.open('GET', ICON_LINK, true);
      xhttp.send();
    });
  }

  iterateLinks(func) {
    let i = this._loadingLinkCount;
    // eslint-disable-next-line no-restricted-syntax
    for (const sidenavLink of this.body.children) {
      if (i > 0) {
        // eslint-disable-next-line no-plusplus
        i--;
        // eslint-disable-next-line no-continue
        continue;
      }
      func(sidenavLink);
    }
  }

  partialDisable() {
    closeAllTabs();
    const overlay = document.querySelector('sb-sidenav')?.shadowRoot?.querySelector('.sb-sidenav').querySelector('.sb-sidenav__overlay');
    overlay?.classList.remove('sb-sidenav__overlay--active');

    this.iterateLinks((sidenavLink) => {
      if (sidenavLink.root && sidenavLink.variation === 'parent') {
        sidenavLink.partialDisable();
      }
    });
  }

  setToggle() {
    const width = window.innerWidth;

    if (width >= BREAKPOINTS.md) {
      this.toggleSidenav(true);
      this.header.collapse();
    }

    this.iterateLinks((sidenavLink) => {
      if (sidenavLink.root && sidenavLink.variation === 'parent') {
        const strategyWillChange = sidenavLink.toggleStrategy !== 'normal';
        if (strategyWillChange) {
          switch (sidenavLink.toggleStrategy) {
            case 'popover':
            case 'account-switch':
              sidenavLink.transitionToNormal();
              break;
            case 'normal':
            default:
              sidenavLink.transitionToPopper();
              break;
          }
        } else {
          sidenavLink.partialEnable();
        }

        if (sidenavLink.type === 'popover') {
          // eslint-disable-next-line no-param-reassign
          sidenavLink.toggleStrategy = width <= BREAKPOINTS.md ? 'normal' : 'popover';
        } else if (sidenavLink.type === 'account-switch') {
          // eslint-disable-next-line no-param-reassign
          sidenavLink.toggleStrategy = width <= BREAKPOINTS.md ? 'normal' : 'account-switch';
        } else {
          // eslint-disable-next-line no-param-reassign
          sidenavLink.toggleStrategy = 'normal';
        }
      }
    });
  }

  setAppHeight() {
    this.root.style.setProperty('--app-height', `${window.innerHeight}px`);
  }

  setupListeners() {
    const url = window.location.href.toLowerCase();

    this.iterateLinks((sidenavLink) => {
      if (sidenavLink.root) {
        if (sidenavLink.variation === 'parent') {
          sidenavLink.setupListeners();
          // On load, open Parent Box when SubLink is selected.
          sidenavLink.sub?.querySelectorAll('sb-sidenav-link').forEach((subLink) => {
            if (sidenavLink.type !== 'popover' && sidenavLink.type !== 'account-switch' && subLink.link && url.includes(subLink.link.toLowerCase())) {
              sidenavLink.toggle();
            }
          });
        }

        this.setActiveLink();
      }
    });
  }

  toggleSidenav(forceCollapsed = false) {
    if (this._expanded || forceCollapsed) {
      this.body.classList.remove('sb-sidenav__body--expanded');
      this.footerWrapper.classList.remove('sb-sidenav__footer-wrapper--expanded');
      this._expanded = false;
    } else {
      this.body.classList.add('sb-sidenav__body--expanded');
      this.footerWrapper.classList.add('sb-sidenav__footer-wrapper--expanded');
      this._expanded = true;
    }
  }

  filterSubaccounts(target) {
    if (target.value !== '' && target.value.length < 3) {
      return;
    }
    const accountSwitchSubMenu = document.querySelector('sb-sidenav')?.shadowRoot?.querySelector('.sb-sidenav')
      .querySelector('.account-switch-sub-menu');
    Array.from(accountSwitchSubMenu.children).forEach((switchOption) => {
      if (switchOption.attributes.name.value.toUpperCase().includes(target.value.toUpperCase())) {
        switchOption.classList.remove('hide');
      } else {
        switchOption.classList.add('hide');
      }
    });
  }
}
installComponent('sb-sidenav', SideNav);
